/**
 * Rotation-aware gateway-HMAC secret provider (D356, amends D192).
 *
 * The stateless sign/verify trio in `./index` requires the caller to pass raw
 * secret bytes. D356 makes that the ONE thing every gateway-plane consumer
 * (edge + the 7 downstreams) no longer hand-rolls: this provider owns the
 * secret lifecycle so rotation is uniform + no-redeploy across the platform.
 *
 *   - FETCH the platform secret `nodii/<env>/gateway/hmac_secret` from AWS
 *     Secrets Manager (JSON `{current, previous, rotated_at}`). The producer
 *     (nodii-infra) promotes `previous <- current`, `current <- new` IN the
 *     secret on each rotation, so the fetched value is authoritative — the
 *     provider MIRRORS it (no client-side promotion).
 *   - CACHE current + previous + rotated_at; SIGN with current; VERIFY with
 *     the stateless dual-verify (current, then previous within the 24h grace).
 *   - RE-FETCH on demand via {@link GatewayHmacRotationProvider.onRotationStarted}
 *     — the consumer wires the `gateway.hmac.rotation_started.v1` trigger event
 *     (a trigger only; carries NO key material) to that method with a one-liner.
 *     The event TRANSPORT (Redis Streams / outbox) is consumer-owned, so the
 *     provider does not couple to a specific bus.
 *   - FALLBACK safety net: fetch-on-startup (fail-fast) + a slow background poll
 *     (~10 min) so a consumer that was down during the event still converges.
 *
 * R1: the default fetcher hits a REAL AWS Secrets Manager (or a localstack
 * endpoint in tests). There is NO Noop/in-memory default — a provider that
 * cannot reach its secret store fails loudly at `start()`.
 *
 * Refs: D356 (locked 2026-06-15), D192/D353/D355 (the bundle), requests
 * bf4874ea (hub) + cf0fff62 (edge).
 */

import {
  GetSecretValueCommand,
  SecretsManagerClient,
} from "@aws-sdk/client-secrets-manager";
import {
  GATEWAY_HMAC_ROTATION_GRACE_MS,
  type GatewayHmacSecrets,
  signGatewayBundle,
  verifyGatewayBundle,
  type VerifyGatewayBundleResult,
} from "./index";

/** Default background poll interval (D356 fallback): 10 minutes. */
export const GATEWAY_HMAC_POLL_INTERVAL_MS = 10 * 60 * 1000;

/**
 * The on-the-wire shape of `nodii/<env>/gateway/hmac_secret` (D356). Secret
 * VALUES are strings; `rotated_at` is ISO-8601 (snake_case matches the stored
 * JSON contract — do NOT camelCase it).
 */
export interface GatewayHmacSecretMaterial {
  current: string;
  previous?: string | null;
  rotated_at?: string | null;
}

/**
 * Reads {@link GatewayHmacSecretMaterial} from a backing store. Abstracted so
 * the provider is unit-testable against a fake without an AWS dependency, while
 * the shipped default ({@link AwsGatewayHmacSecretFetcher}) hits real Secrets
 * Manager.
 */
export interface GatewayHmacSecretFetcher {
  fetch(): Promise<GatewayHmacSecretMaterial>;
}

/** Thrown when the rotation provider is used before {@link GatewayHmacRotationProvider.start}. */
export class GatewayHmacProviderNotStartedError extends Error {
  constructor() {
    super(
      "GatewayHmacRotationProvider: not started — call await provider.start() " +
        "(fetch-on-startup) before sign/verify/getSecrets.",
    );
    this.name = "GatewayHmacProviderNotStartedError";
  }
}

/** Thrown when the fetched secret material is missing the required `current`. */
export class GatewayHmacSecretMalformedError extends Error {
  constructor(detail: string) {
    super(`gateway HMAC secret malformed: ${detail}`);
    this.name = "GatewayHmacSecretMalformedError";
  }
}

/**
 * Storage contract (D356): the gateway HMAC secret material
 * (`nodii/<env>/gateway/hmac_secret` JSON `current`/`previous`) is stored as
 * **standard base64** of the raw key bytes. That is the SAME encoding the
 * downstream env verifiers consume — edge's `decodeSecret`
 * (`Buffer.from(EDGE_GATEWAY_HMAC_SECRET, "base64")`) and auth's gateway-trust
 * `toLibSecrets` (`Buffer.from(GATEWAY_HMAC_SECRET_CURRENT, "base64")`). So for
 * ONE stored value the SM-provider path (here) and the env-verifier path derive
 * IDENTICAL key bytes — the whole point of D356 (uniform secret lifecycle).
 *
 * Decode discipline — STRICT-canonical-base64-with-UTF-8-fallback:
 *   1. If `value` is canonical standard base64 (charset `[A-Za-z0-9+/]` + up to
 *      two `=` pads, length a multiple of 4, and a round-trip re-encode matches
 *      the input byte-for-byte) → return the base64-decoded bytes.
 *   2. Otherwise (non-base64 ASCII, url-safe `-_`, whitespace, wrong length) →
 *      fall back to UTF-8 bytes.
 * The round-trip guard mirrors the exact discipline in this package's
 * `decodeUserActorEnvelope` (charset + `len % 4` guard, `Buffer.from` is lenient
 * so it must be validated). The UTF-8 fallback keeps a legacy ASCII-in-SM secret
 * working while the platform migrates to the stored-base64 contract; once the
 * secret is stored base64 (the contract), both paths agree.
 */
export function defaultDecodeSecret(value: string): Uint8Array {
  const decoded = tryStrictStandardBase64(value);
  return decoded ?? new TextEncoder().encode(value);
}

/**
 * Return the base64-decoded bytes iff `value` is canonical standard base64,
 * else `null`. Canonical = charset `[A-Za-z0-9+/]` with 0–2 trailing `=`,
 * length a multiple of 4, and re-encoding the decoded bytes reproduces `value`
 * exactly (so `Buffer.from`'s leniency — dropping stray chars, accepting
 * base64url `-_`, missing padding — cannot slip through). Empty string is NOT
 * treated as base64 (falls through to UTF-8, i.e. zero bytes either way).
 */
function tryStrictStandardBase64(value: string): Uint8Array | null {
  if (value.length === 0) return null;
  if (value.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(value)) {
    return null;
  }
  const buf = Buffer.from(value, "base64");
  // Round-trip: canonical standard base64 re-encodes to itself byte-for-byte.
  if (buf.toString("base64") !== value) return null;
  return new Uint8Array(buf);
}

export interface AwsGatewayHmacSecretFetcherOptions {
  /** Deployment env → secret id `nodii/<env>/gateway/hmac_secret`. */
  env: string;
  region: string;
  /** Endpoint override for localstack / non-prod AWS-compatible targets. */
  endpoint?: string;
  /** Explicit creds; omit to use the SDK default provider chain. */
  credentials?: { accessKeyId: string; secretAccessKey: string };
  /** Full secret id override (defaults to `nodii/<env>/gateway/hmac_secret`). */
  secretId?: string;
}

/**
 * The shipped {@link GatewayHmacSecretFetcher} — reads the gateway HMAC secret
 * from real AWS Secrets Manager. Mirrors `@nodii/pii`'s AWS client config
 * (region + optional localstack endpoint + optional explicit creds).
 */
export class AwsGatewayHmacSecretFetcher implements GatewayHmacSecretFetcher {
  private readonly client: SecretsManagerClient;
  private readonly secretId: string;

  constructor(opts: AwsGatewayHmacSecretFetcherOptions) {
    this.client = new SecretsManagerClient({
      region: opts.region,
      ...(opts.endpoint ? { endpoint: opts.endpoint } : {}),
      ...(opts.credentials ? { credentials: opts.credentials } : {}),
    });
    this.secretId = opts.secretId ?? `nodii/${opts.env}/gateway/hmac_secret`;
  }

  async fetch(): Promise<GatewayHmacSecretMaterial> {
    const res = await this.client.send(
      new GetSecretValueCommand({ SecretId: this.secretId }),
    );
    if (typeof res.SecretString !== "string") {
      throw new GatewayHmacSecretMalformedError(
        `${this.secretId} has no SecretString`,
      );
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(res.SecretString);
    } catch {
      throw new GatewayHmacSecretMalformedError(
        `${this.secretId} SecretString is not valid JSON`,
      );
    }
    const obj = parsed as Record<string, unknown>;
    if (typeof obj.current !== "string" || obj.current.length === 0) {
      throw new GatewayHmacSecretMalformedError(
        `${this.secretId} JSON missing a non-empty string "current"`,
      );
    }
    return {
      current: obj.current,
      previous: typeof obj.previous === "string" ? obj.previous : null,
      rotated_at: typeof obj.rotated_at === "string" ? obj.rotated_at : null,
    };
  }
}

export interface GatewayHmacRotationProviderOptions {
  /** Secret source. Use {@link AwsGatewayHmacSecretFetcher} in production. */
  fetcher: GatewayHmacSecretFetcher;
  /** Background poll interval (missed-event fallback). Default 10 min. */
  pollIntervalMs?: number;
  /** Dual-verify grace window. Default 24h ({@link GATEWAY_HMAC_ROTATION_GRACE_MS}). */
  rotationGraceMs?: number;
  /** SM string secret → bytes. Default {@link defaultDecodeSecret}
   *  (strict-canonical-base64 with UTF-8 fallback; matches the env verifiers). */
  decodeSecret?: (value: string) => Uint8Array;
  /** Sink for non-fatal background poll/rotation fetch failures. */
  onError?: (err: unknown, ctx: "poll" | "rotation") => void;
}

/**
 * Stateful, rotation-aware gateway-HMAC provider. Lifecycle:
 *   `const p = new GatewayHmacRotationProvider({ fetcher }); await p.start();`
 * then `p.sign(...)` / `p.verify(...)`, and wire the rotation event:
 *   `onEvent("gateway.hmac.rotation_started.v1", () => p.onRotationStarted())`.
 * Call `p.stop()` on shutdown to clear the poll timer.
 */
export class GatewayHmacRotationProvider {
  private readonly fetcher: GatewayHmacSecretFetcher;
  private readonly pollIntervalMs: number;
  private readonly rotationGraceMs: number;
  private readonly decodeSecret: (value: string) => Uint8Array;
  private readonly onError: (err: unknown, ctx: "poll" | "rotation") => void;
  private secrets: GatewayHmacSecrets | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(opts: GatewayHmacRotationProviderOptions) {
    this.fetcher = opts.fetcher;
    this.pollIntervalMs = opts.pollIntervalMs ?? GATEWAY_HMAC_POLL_INTERVAL_MS;
    this.rotationGraceMs =
      opts.rotationGraceMs ?? GATEWAY_HMAC_ROTATION_GRACE_MS;
    this.decodeSecret = opts.decodeSecret ?? defaultDecodeSecret;
    this.onError =
      opts.onError ??
      ((err, ctx) => {
        // eslint-disable-next-line no-console
        console.warn(
          `[@nodii/grpc-auth] gateway HMAC ${ctx} refresh failed; serving last-known secrets:`,
          err,
        );
      });
  }

  /**
   * Fetch-on-startup (fail-fast — throws if the secret store is unreachable or
   * the secret is malformed) then arm the background poll. Idempotent: a second
   * call refreshes + leaves a single timer.
   */
  async start(): Promise<void> {
    await this.refresh();
    if (this.timer === null) {
      this.timer = setInterval(() => {
        this.refresh().catch((err) => this.onError(err, "poll"));
      }, this.pollIntervalMs);
      // Don't keep the event loop alive on the poll timer alone.
      this.timer.unref?.();
    }
  }

  /** Stop the background poll. Safe to call repeatedly. */
  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /** The cached secret set. Throws if not started. */
  getSecrets(): GatewayHmacSecrets {
    if (this.secrets === null) {
      throw new GatewayHmacProviderNotStartedError();
    }
    return this.secrets;
  }

  /** Sign a bundle with the CURRENT secret (edge always signs current). */
  sign(headers: Record<string, string>, order: string[]): string {
    return signGatewayBundle({
      headers,
      order,
      secret: this.getSecrets().current,
    });
  }

  /** Dual-verify a bundle against current, then previous within the grace. */
  verify(
    headers: Record<string, string>,
    order: string[],
    signature: string,
  ): VerifyGatewayBundleResult {
    return verifyGatewayBundle({
      headers,
      order,
      signature,
      secrets: this.getSecrets(),
      rotationGraceMs: this.rotationGraceMs,
    });
  }

  /**
   * Re-fetch the secret immediately. Wire the consumer's
   * `gateway.hmac.rotation_started.v1` subscription to this. A failure here is
   * surfaced via `onError("rotation", ...)` and does NOT throw — the poll +
   * last-known secrets keep verification working until the next success.
   */
  async onRotationStarted(): Promise<void> {
    try {
      await this.refresh();
    } catch (err) {
      this.onError(err, "rotation");
    }
  }

  private async refresh(): Promise<void> {
    const material = await this.fetcher.fetch();
    this.secrets = {
      current: this.decodeSecret(material.current),
      previous: material.previous ? this.decodeSecret(material.previous) : null,
      rotatedAt: material.rotated_at ?? null,
    };
  }
}
